// src/data/worksheets/nutrition.js — performance-side pair (authored).
// Built off the Nutrition action plan: eat before you're empty, water first, one added not one removed.

export const NUTRITION_CHECKLIST = {
    id: 'nutrition_checklist', topicId: 'nutrition-hydration', kind: 'checklist',
    title: 'Nutrition & Hydration Checklist',
    intro: 'Small, regular changes matter more than a perfect diet. Tick these off as you build them in.',
    sections: [
        { id: 'regular', title: 'Eat Regularly', prompts: [
            { id: 'empty', type: 'checklist', label: 'Eat Before You\'re Empty', items: [
                { id: 'e1', label: 'Don\'t go more than four hours without something, especially on busy days.' },
                { id: 'e2', label: 'Keep one easy snack with you (fruit, nuts, yoghurt, a bar).' },
                { id: 'e3', label: 'Eat something within an hour of waking, even if it\'s small.' }
            ] },
            { id: 'around', type: 'checklist', label: 'Fuel Around Effort', items: [
                { id: 'a1', label: 'Something with carbohydrate 1–3 hours before {training or a demanding block|a demanding block of work}.' },
                { id: 'a2', label: 'Protein plus carbohydrate within an hour or two afterwards.' }
            ] }
        ] },
        { id: 'water', title: 'Water First', prompts: [
            { id: 'hydrate', type: 'checklist', label: 'Hydration Habits', items: [
                { id: 'h1', label: 'A glass of water when you wake up.' },
                { id: 'h2', label: 'A glass with each meal.' },
                { id: 'h3', label: 'Carry a bottle and refill it — sip through the day rather than catching up at night.' },
                { id: 'h4', label: 'Pale-yellow urine is the simplest check.' }
            ] }
        ] },
        { id: 'add', title: 'Add, Don\'t Remove', prompts: [
            { id: 'plus', type: 'checklist', label: 'One Added, Not One Removed', items: [
                { id: 'p1', label: 'Add a vegetable or a protein to a meal you already eat rather than cutting things out.' },
                { id: 'p2', label: 'Half the plate colourful at one meal a day.' },
                { id: 'p3', label: 'Pick one "upgrade" for the week (e.g., water instead of the second soft drink).' }
            ] },
            { id: 'plan', type: 'checklist', label: 'Make It Easy', items: [
                { id: 'm1', label: 'Plan two or three go-to meals you can make in 15 minutes.' },
                { id: 'm2', label: 'Shop once with a list. Decisions at the store beat decisions at 9pm.' }
            ] }
        ] },
        { id: 'awareness', title: 'Notice the Links', prompts: [
            { id: 'energy', type: 'checklist', label: 'Food and Energy', items: [
                { id: 'en1', label: 'Notice the afternoon slump — it usually traces back to lunch or hydration.' },
                { id: 'en2', label: 'Notice mood dips when you\'ve skipped a meal.' }
            ] },
            { id: 'mindful', type: 'checklist', label: 'Eat Like You Mean It', items: [
                { id: 'mi1', label: 'One meal a day without a screen.' },
                { id: 'mi2', label: 'Slow down enough to notice when you\'re full.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Look at the week\'s log. Which days had the best energy, and what did you eat?' },
                { id: 'wk2', label: 'Pick next week\'s one upgrade.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'You don\'t need a perfect diet. You need to be fuelled and hydrated most of the time.',
    supportNote: 'If eating has become stressful or something you feel you need to control tightly, talking to someone is the right move.'
};

export const NUTRITION_WORKSHEET = {
    id: 'nutrition_worksheet', topicId: 'nutrition-hydration', kind: 'worksheet',
    title: 'Nutrition & Hydration Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'energy', type: 'scale', label: 'How has your energy been over the past week?', min: 1, max: 10, minLabel: 'Flat all day', maxLabel: 'Steady and strong' },
            { id: 'typical', type: 'list', label: 'A typical day of eating', help: 'Roughly what and when.', count: 4, itemLabels: ['Morning', 'Midday', 'Afternoon', 'Evening'] },
            { id: 'water', type: 'text', label: 'Roughly how much water you drink in a day' },
            { id: 'problems', type: 'list', label: 'Where it falls apart', help: 'e.g., skipping breakfast, nothing until 3pm, late-night snacking, energy drinks.', count: 3 }
        ] },
        { id: 'understanding', title: 'Understanding the Links', prompts: [
            { id: 'slump', type: 'text', label: 'When do you feel the slump, and what came before it?', multiline: true },
            { id: 'best', type: 'text', label: 'Your best-energy day recently — what did you eat and drink?', help: 'This is your template.', multiline: true },
            { id: 'barriers', type: 'list', label: 'What makes eating well hard right now', help: 'Time, money, cooking, travel, schedule.', count: 3 }
        ] },
        { id: 'building', title: 'Building the Habits', prompts: [
            { id: 'meals', type: 'list', label: 'Three go-to meals you can make in 15 minutes', count: 3 },
            { id: 'snack', type: 'text', label: 'The snack you will keep with you' },
            { id: 'upgrade', type: 'text', label: 'This week\'s one upgrade', help: 'One added, not one removed.' },
            { id: 'hydration', type: 'list', label: 'Your hydration anchors', help: 'When you will drink water without thinking (e.g., on waking, each meal, {after training|after work}).', count: 3 }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'goal', type: 'text', label: 'This week\'s nutrition goal', help: 'e.g., "nothing longer than four hours without food", "2 litres a day".' },
            { id: 'shop', type: 'text', label: 'When you will shop, and the five things always on the list', multiline: true },
            { id: 'support', type: 'text', label: 'Who eats with you or can help', help: 'A partner, family, a {teammate|colleague}, a dietitian.' }
        ] },
        { id: 'track', title: 'Track It', description: 'Meals, water and energy. The chart shows whether fuelling regularly moves your energy.', prompts: [
            { id: 'tracker', type: 'table', label: 'Daily fuel log', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'meals', label: 'Meals + snacks', kind: 'number', min: 0, max: 8 },
                { id: 'water', label: 'Water (litres)', kind: 'number', min: 0, max: 6 },
                { id: 'energy', label: 'Energy (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'energy', label: 'Energy' }, { id: 'meals', label: 'Meals' }, { id: 'water', label: 'Water (L)' }] } }
        ] }
    ],
    outro: 'Fuelled and hydrated most of the time — that is the whole target.',
    supportNote: 'If eating has become stressful or something you feel you need to control tightly, talking to someone is the right move.'
};

export const NUTRITION_WORKSHEETS = [NUTRITION_CHECKLIST, NUTRITION_WORKSHEET];
